

export const calculatePolygonArea = (vertices) => {
    if (!vertices || vertices.length < 3) {
        return 0;
    }
    let area = 0;
    const n = vertices.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += vertices[i].x * vertices[j].y;
        area -= vertices[j].x * vertices[i].y;
    }
    return Math.abs(area / 2);
};

export const rotatePoint = (v, rx, ry) => {
    // Rotation matrices: Rx(rx) then Ry(ry)
    // Rx (tilt around X axis, i.e., Pitch equivalent)
    const y1 = v.y * Math.cos(rx) - v.z * Math.sin(rx);
    const z1 = v.y * Math.sin(rx) + v.z * Math.cos(rx);
    
    // Ry (tilt around Y axis, i.e., Roll equivalent)
    const x2 = v.x * Math.cos(ry) + z1 * Math.sin(ry);
    const z2 = -v.x * Math.sin(ry) + z1 * Math.cos(ry);
    
    return { x: x2, y: y1, z: z2 };
};

export const projectPoint = (v, rx, ry, planeDistance) => {
    const rotated = rotatePoint(v, rx, ry);
    if (rotated.z <= 0) {
        // Point is looking back or parallel, projection goes to infinity
        return { x: rotated.x > 0 ? Infinity : -Infinity, y: rotated.y > 0 ? Infinity : -Infinity };
    }
    return {
        x: (rotated.x / rotated.z) * planeDistance,
        y: (rotated.y / rotated.z) * planeDistance
    };
};

const getBoundingBox = (poly) => {
    const xs = poly.map(p => p.x);
    const ys = poly.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return {
        minX, maxX, minY, maxY,
        width: maxX - minX,
        height: maxY - minY,
        aveWidth: (Math.abs(poly[1].x - poly[2].x) + Math.abs(poly[0].x - poly[3].x)) / 2,
        minWidth: Math.min(Math.abs(poly[1].x - poly[2].x), Math.abs(poly[0].x - poly[3].x)),
        maxWidth: Math.max(Math.abs(poly[1].x - poly[2].x), Math.abs(poly[0].x - poly[3].x)),
        aveHeight: (Math.abs(poly[0].y - poly[1].y) + Math.abs(poly[3].y - poly[2].y)) / 2,
        minHeight: Math.min(Math.abs(poly[0].y - poly[1].y), Math.abs(poly[3].y - poly[2].y)),
        maxHeight: Math.max(Math.abs(poly[0].y - poly[1].y), Math.abs(poly[3].y - poly[2].y)),
    };
};

export const calculateTopDownCoverage = ({ 
    height, 
    personHeight, 
    xTiltDeg, 
    yTiltDeg, 
    halfWidth1m, 
    halfHeight1m 
}) => {
    const rx = (xTiltDeg || 0) * Math.PI / 180;
    const ry = (yTiltDeg || 0) * Math.PI / 180;
    
    // Excel formula for Auto Magnification: =MAX(1, 25 / (0.4 / (H - 1.2) / (HW * 2) * 512))
    let autoMagnificationFactor = 1;
    if (height > 1.2) {
        autoMagnificationFactor = Math.max(1, 25 / (0.4 / (height - 1.2) / (halfWidth1m * 2) * 512));
    }

    const hw = halfWidth1m / autoMagnificationFactor;
    const hh = halfHeight1m / autoMagnificationFactor;

    const baseVectors = [
        { x: hw, y: hh, z: 1 },    // P1
        { x: hw, y: -hh, z: 1 },   // P2
        { x: -hw, y: -hh, z: 1 },  // P3
        { x: -hw, y: hh, z: 1 }    // P4
    ];

    // Excel specifically maps xTilt physically to the first matrix rot, and yTilt to the second.
    // It evaluates exactly as Ry(yTilt) * Rx(xTilt) which swaps common axis mappings.
    const R = [
        [Math.cos(ry), Math.sin(ry) * Math.sin(rx), Math.sin(ry) * Math.cos(rx)],
        [0, Math.cos(rx), -Math.sin(rx)],
        [-Math.sin(ry), Math.cos(ry) * Math.sin(rx), Math.cos(ry) * Math.cos(rx)]
    ];

    const rotatedVectors = baseVectors.map(p => ({
        x: R[0][0]*p.x + R[0][1]*p.y + R[0][2]*p.z,
        y: R[1][0]*p.x + R[1][1]*p.y + R[1][2]*p.z,
        z: R[2][0]*p.x + R[2][1]*p.y + R[2][2]*p.z
    }));
    
    const shoulderDistance = Math.max(0, height - personHeight);
    
    const L5_sq = (Math.pow(hw, 2) + Math.pow(hh, 2)) * Math.pow(shoulderDistance, 2) + Math.pow(shoulderDistance, 2);
    const Dmax2_H2 = L5_sq - Math.pow(shoulderDistance, 2);

    let isVisionValid = true;
    let isCoverageValid = true;

    const coverageFieldPoly = rotatedVectors.map(rp => {
        if (rp.z <= 0) {
            isVisionValid = false;
            isCoverageValid = false;
            return { x: Infinity, y: Infinity };
        }
        
        const raw_x = rp.x * shoulderDistance / rp.z;
        const raw_y = rp.y * shoulderDistance / rp.z;
        
        const t_shoulder = shoulderDistance / rp.z;
        const t_prime = (Dmax2_H2 > 0 && (raw_x*raw_x + raw_y*raw_y) > 0) 
                        ? Math.sqrt(Dmax2_H2 / (raw_x*raw_x + raw_y*raw_y)) : 0;
        
        const useRaw = t_prime > 1 && t_shoulder > 0;
        const x = useRaw ? raw_x : Math.sign(t_shoulder) * t_prime * raw_x;
        const y = useRaw ? raw_y : Math.sign(t_shoulder) * t_prime * raw_y;
        
        // Exact floating point rounds matched to Excel
        return { x: Number(x.toFixed(2)), y: Number(y.toFixed(2)) };
    });

    const visionFieldPoly = rotatedVectors.map((rp, i) => {
        if (rp.z <= 0) return { x: Infinity, y: Infinity };
        
        if (shoulderDistance > 0) {
            const cvg = coverageFieldPoly[i];
            return { 
                x: Number((cvg.x * height / shoulderDistance).toFixed(2)),
                y: Number((cvg.y * height / shoulderDistance).toFixed(2)) 
            };
        } else {
            return { x: Number((rp.x * height / rp.z).toFixed(2)), y: Number((rp.y * height / rp.z).toFixed(2)) };
        }
    });

    if (shoulderDistance <= 0) isCoverageValid = false;

    let visionFieldStats = null;
    let coverageFieldStats = null;

    if (isVisionValid) {
        visionFieldStats = {
            ...getBoundingBox(visionFieldPoly),
            area: calculatePolygonArea(visionFieldPoly),
            polygon: visionFieldPoly
        };
    }

    if (isCoverageValid) {
        coverageFieldStats = {
            ...getBoundingBox(coverageFieldPoly),
            area: calculatePolygonArea(coverageFieldPoly),
            polygon: coverageFieldPoly
        };
    }

    return {
        isValid: isCoverageValid,
        error: isCoverageValid ? null : "Target height invalid or angle too steep.",
        visionField: visionFieldStats,
        coverageField: coverageFieldStats
    };
};
